import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router-dom';

import api from 'api';
import { HTTPError } from 'api/error';
import {
  type AgentCreationConfig,
  createAgent,
} from 'api/resources/agentCreation';
import type { AgentSetupStatusResponse } from 'api/resources/agentWizard/types';
import { LOGGER_TYPE } from 'api/types';
import useAgentWizardActiveJob from 'hooks/useAgentWizardActiveJob';
import { useIsPlgProd } from 'hooks/useIsPlgProd';
import { useToast } from 'hooks/useToast';
import { logToDataDog } from 'lib/ddlogHelper';
import { POLLING_CONFIG, RETRY_CONFIG } from 'screens/AgentWizard/constants';
import { usePersonalAccount } from 'screens/AgentWizard/hooks';
import { getUserDisplayName } from 'screens/AgentWizard/utils/userName';

// Types
interface AgentSetupParams {
  projectId: string;
  companyUrl?: string;
}

export interface AgentSetupContextValue {
  setupStatus: AgentSetupStatusResponse | null;
  retryCount: number;
  hasInitiallyChecked: boolean;
  initializeAgentSetup: (params: AgentSetupParams) => Promise<void>;
  retry: () => void;
  cancelSetup: () => void;
  createWizardAgent: (
    props: AgentCreationConfig,
  ) => Promise<string | undefined>;
  isCreating: boolean;
}

interface ProviderProps {
  children: React.ReactNode;
  enableToasts?: boolean;
}

// Context
const AgentSetupContext = createContext<AgentSetupContextValue | undefined>(
  undefined,
);

// Provider component
const Provider = (props: ProviderProps) => {
  const { accountId: routeAccountId } = useParams<{ accountId?: string }>();
  const personalAccountId = usePersonalAccount('');
  const accountId = routeAccountId ?? personalAccountId;
  const { user } = useAuth0();
  const userName = useMemo(() => getUserDisplayName(user), [user]);
  const { setActiveJob } = useAgentWizardActiveJob();
  const toast = useToast();

  const [setupStatus, setSetupStatus] =
    useState<AgentSetupStatusResponse | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [hasInitiallyChecked, setHasInitiallyChecked] = useState(false);
  const [creatingByAccount, setCreatingByAccount] = useState<
    Record<string, boolean>
  >({});

  const currentProjectIdRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef<number>(0);
  const isPollingRef = useRef<boolean>(false);
  const isSetupInProgressRef = useRef<boolean>(false);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    isPollingRef.current = false;
    pollingAttemptsRef.current = 0;
  }, []);

  const cancelSetup = useCallback(() => {
    if (!currentProjectIdRef.current) {
      return;
    }

    logToDataDog(LOGGER_TYPE.PROJECT, 'info', 'Canceling agent-setup', {
      projectId: currentProjectIdRef.current,
    });

    stopPolling();
    setSetupStatus(null);
    setRetryCount(0);
    setHasInitiallyChecked(false);
    isSetupInProgressRef.current = false;
    currentProjectIdRef.current = null;
  }, [stopPolling]);

  const fetchJob =
    useCallback(async (): Promise<AgentSetupStatusResponse | null> => {
      const projectId = currentProjectIdRef.current;
      if (!accountId || !projectId) {
        return null;
      }

      const job = await api.agentWizardAgentSetupStatus({
        accountId,
        projectId,
      });
      const newSetupStatus = processJobResponse(job);

      // Make sure the project hasn't changed or cancelled during fetch
      if (currentProjectIdRef.current !== projectId) {
        return null;
      }

      if (!statusIsInProgress(newSetupStatus)) {
        isSetupInProgressRef.current = false;
        stopPolling();
        setActiveJob();

        if (props.enableToasts && newSetupStatus.status === 'completed') {
          toast.success({
            title: AGENT_SETUP_CONTEXT.toast.success.title,
            customId: getAgentSetupToastId(projectId),
          });
        }
      }

      setSetupStatus(newSetupStatus);

      logToDataDog(
        LOGGER_TYPE.PROJECT,
        newSetupStatus.status === 'failed' ? 'error' : 'info',
        `Agent-setup job ${newSetupStatus.status}`,
        { projectId: newSetupStatus.params.projectId },
      );

      return newSetupStatus;
    }, [accountId, stopPolling, setActiveJob, props.enableToasts, toast]);

  const handleJobFailed = useCallback(
    (errorMessage: string) => {
      logToDataDog(LOGGER_TYPE.PROJECT, 'error', 'Agent-setup job failed', {
        error: errorMessage,
      });

      isSetupInProgressRef.current = false;

      setSetupStatus(
        prev =>
          prev && {
            ...prev,
            error: errorMessage,
            status: 'failed',
          },
      );

      if (props.enableToasts && currentProjectIdRef.current) {
        toast.error({
          title: AGENT_SETUP_CONTEXT.toast.failed.title,
          customId: getAgentSetupToastId(currentProjectIdRef.current),
        });
      }
    },
    [props.enableToasts, toast],
  );

  const pollJobStatus = useCallback(
    // biome-ignore format: force wrap, small diff
    async (): Promise<void> => {
      // Prevent concurrent polling
      if (isPollingRef.current) {
        return;
      }
      isPollingRef.current = true;

      const poll = async () => {
        pollingAttemptsRef.current++;

        // Check if max attempts reached
        if (pollingAttemptsRef.current > POLLING_CONFIG.MAX_ATTEMPTS) {
          stopPolling();
          const timeoutMinutes =
            (POLLING_CONFIG.MAX_ATTEMPTS * POLLING_CONFIG.INTERVAL_MS) / 60000;
          handleJobFailed(
            `Agent setup timed out after ${timeoutMinutes} minutes`,
          );
          return;
        }

        try {
          await fetchJob();
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);

          logToDataDog(
            LOGGER_TYPE.PROJECT,
            'warn',
            'Error polling agent-setup status',
            {
              error: errorMessage,
              attempt: pollingAttemptsRef.current,
            },
          );

          // Don't stop polling on transient errors, let it retry
          // Only stop if we've exhausted attempts
          if (pollingAttemptsRef.current > POLLING_CONFIG.MAX_ATTEMPTS) {
            stopPolling();
            handleJobFailed(`Failed to check job status: ${errorMessage}`);
          }
        }
      };

      // Start polling interval
      pollingIntervalRef.current = setInterval(
        () => void poll(),
        POLLING_CONFIG.INTERVAL_MS,
      );
    },
    [stopPolling, fetchJob, handleJobFailed],
  );

  const performAgentSetup = useCallback(
    async (
      params: Required<AgentSetupParams>,
      attemptNumber = 0,
    ): Promise<void> => {
      if (!accountId) {
        return;
      }

      try {
        logToDataDog(
          LOGGER_TYPE.PROJECT,
          'info',
          'Starting agent-setup API call',
          {
            params,
            attemptNumber,
          },
        );

        // Call the setup endpoint to start the job
        // Cache behavior is controlled by backend feature flags
        const job = await api.agentWizardAgentSetup({
          accountId: accountId,
          projectId: params.projectId,
          body: {
            companyUrl: params.companyUrl,
            userName,
          },
        });

        const newSetupStatus = processJobResponse(job);
        setSetupStatus(newSetupStatus);
        setActiveJob(params.projectId);

        if (statusIsInProgress(newSetupStatus)) {
          logToDataDog(
            LOGGER_TYPE.PROJECT,
            'info',
            'Agent-setup job created, starting polling',
            {},
          );
          await pollJobStatus();
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);

        // Implement automatic retry with exponential backoff for network errors
        const isNetworkError =
          error instanceof Error &&
          (error.message.includes('fetch') ||
            error.message.includes('network') ||
            error.message.includes('timeout'));

        if (isNetworkError && attemptNumber < RETRY_CONFIG.MAX_RETRIES) {
          const backoffDelay = Math.min(
            RETRY_CONFIG.INITIAL_BACKOFF_MS * Math.pow(2, attemptNumber),
            RETRY_CONFIG.MAX_BACKOFF_MS,
          );

          logToDataDog(
            LOGGER_TYPE.PROJECT,
            'warn',
            'Agent-setup failed, retrying with exponential backoff',
            {
              error: errorMessage,
              attemptNumber,
              nextAttempt: attemptNumber + 1,
              backoffDelay,
            },
          );

          setRetryCount(attemptNumber + 1);
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return performAgentSetup(params, attemptNumber + 1);
        }

        logToDataDog(
          LOGGER_TYPE.PROJECT,
          'error',
          'Error initiating agent-setup',
          {
            error: errorMessage,
            params,
            attemptNumber,
            maxRetriesReached: attemptNumber >= RETRY_CONFIG.MAX_RETRIES,
          },
        );

        handleJobFailed(errorMessage);
      }
    },
    [accountId, userName, handleJobFailed, pollJobStatus, setActiveJob],
  );

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const initializeAgentSetup = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    async (params: AgentSetupParams) => {
      // Reset state for new project
      if (currentProjectIdRef.current !== params.projectId) {
        cancelSetup();
      }
      currentProjectIdRef.current = params.projectId;

      // Synchronous guard to prevent race conditions
      if (isSetupInProgressRef.current) {
        logToDataDog(
          LOGGER_TYPE.PROJECT,
          'info',
          'Agent-setup already in progress (ref guard)',
          {
            params,
          },
        );
        return;
      }

      // Set synchronous guard
      isSetupInProgressRef.current = true;

      // Only check existing job status when resuming (no companyUrl).
      // When companyUrl is provided we're starting fresh — skip the status check.
      let currentSetupStatus: AgentSetupStatusResponse | null = null;
      if (!params.companyUrl) {
        try {
          currentSetupStatus = await fetchJob();
        } catch (error) {
          const isNotFound =
            error instanceof HTTPError && error.httpStatus === 404;

          if (!isNotFound) {
            logToDataDog(
              LOGGER_TYPE.PROJECT,
              'error',
              'Failed to fetch agent-setup status',
              { error: error instanceof Error ? error.message : String(error) },
            );
            isSetupInProgressRef.current = false;
            throw error;
          }
          // 404 — no existing job found, proceed with setup
        }
      }

      // Abort if projectId has changed
      if (currentProjectIdRef.current !== params.projectId) {
        return;
      }

      setHasInitiallyChecked(true);

      // Check if job already exists after refresh
      if (currentSetupStatus) {
        const { status } = currentSetupStatus;

        logToDataDog(
          LOGGER_TYPE.PROJECT,
          'info',
          'Backend agent-setup status check',
          {
            status,
          },
        );

        switch (status) {
          case 'completed':
            // Status already set by fetchJob
            return;

          case 'processing':
          case 'pending':
            // Job is in progress, resume polling
            logToDataDog(
              LOGGER_TYPE.PROJECT,
              'info',
              'Found in-progress agent-setup job in backend, resuming polling',
              { status },
            );
            await pollJobStatus();
            return;

          case 'failed':
            // Start new setup
            break;

          default:
            // No job exists, start a new one
            logToDataDog(
              LOGGER_TYPE.PROJECT,
              'info',
              'No existing agent-setup job found, starting new job',
              {},
            );
        }
      }

      // Use companyUrl from existing job if available, otherwise from params
      const companyUrl =
        currentSetupStatus?.params.companyUrl ?? params.companyUrl;
      if (!companyUrl) {
        isSetupInProgressRef.current = false;
        logToDataDog(
          LOGGER_TYPE.PROJECT,
          'error',
          'Cannot start agent-setup: no companyUrl available',
          { projectId: params.projectId },
        );
        return;
      }

      await performAgentSetup({ projectId: params.projectId, companyUrl });
    },
    [cancelSetup, fetchJob, performAgentSetup, pollJobStatus],
  );

  const retry = useCallback(() => {
    if (!setupStatus) {
      logToDataDog(
        LOGGER_TYPE.PROJECT,
        'warn',
        'Cannot retry agent-setup: no setupStatus found',
        { projectId: currentProjectIdRef.current },
      );
      return;
    }

    const { projectId, companyUrl } = setupStatus.params;

    logToDataDog(LOGGER_TYPE.PROJECT, 'info', 'Manually retrying agent-setup', {
      projectId,
    });

    // Dismiss any existing toast for this project
    toast.dismiss(getAgentSetupToastId(projectId));

    // Stop any existing polling
    stopPolling();

    // Reset retry count for manual retry
    setRetryCount(0);

    // Set the guard since we're starting a new setup
    isSetupInProgressRef.current = true;

    // Start a new request
    void performAgentSetup({ projectId, companyUrl });
  }, [setupStatus, performAgentSetup, stopPolling, toast]);

  const setCreatingLoading = useCallback(
    (accountId: string, loading: boolean) => {
      setCreatingByAccount(prev => ({ ...prev, [accountId]: loading }));
    },
    [],
  );

  const createWizardAgent = useCallback(
    async (props: AgentCreationConfig) => {
      try {
        setCreatingLoading(props.accountId, true);
        const result = await createAgent(props);
        const projectId = result.agent.id;

        // Initialize agent-setup in the background immediately after project creation
        await initializeAgentSetup({ projectId, companyUrl: props.companyUrl });

        setCreatingLoading(props.accountId, false);
        return projectId;
      } catch (error) {
        setCreatingLoading(props.accountId, false);
        logToDataDog(LOGGER_TYPE.PROJECT, 'error', 'Error creating project', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [initializeAgentSetup, setCreatingLoading],
  );

  const isCreating = useMemo(
    () => (accountId ? (creatingByAccount[accountId] ?? false) : false),
    [creatingByAccount, accountId],
  );

  const contextValue = useMemo(
    () => ({
      setupStatus,
      retryCount,
      hasInitiallyChecked,
      initializeAgentSetup,
      retry,
      cancelSetup,
      createWizardAgent,
      isCreating,
    }),
    [
      setupStatus,
      retryCount,
      hasInitiallyChecked,
      initializeAgentSetup,
      retry,
      cancelSetup,
      createWizardAgent,
      isCreating,
    ],
  );

  return (
    <AgentSetupContext.Provider value={contextValue}>
      {props.children}
    </AgentSetupContext.Provider>
  );
};

export interface AgentSetupProviderProps extends ProviderProps {
  passThrough?: boolean; //render children with no provider outside plg
}

export const AgentSetupProvider = (props: AgentSetupProviderProps) => {
  const isPlgProd = useIsPlgProd();

  return isPlgProd ? (
    <Provider {...props} />
  ) : props.passThrough ? (
    props.children
  ) : null;
};

// Helper functions
const statusIsInProgress = (status: AgentSetupStatusResponse) =>
  status.status !== 'completed' && status.status !== 'failed';

const processJobResponse = (
  newSetupStatus: AgentSetupStatusResponse,
): AgentSetupStatusResponse => {
  // Handle completed jobs without results as failures
  if (newSetupStatus.status === 'completed' && !newSetupStatus.result) {
    return {
      ...newSetupStatus,
      status: 'failed',
      error: 'Job completed but no result was returned',
    };
  }

  return newSetupStatus;
};

const getAgentSetupToastId = (projectId: string) => `agent-setup-${projectId}`;

// Hook to use the context
export const useAgentSetupContext = (): AgentSetupContextValue => {
  const context = useContext(AgentSetupContext);
  if (context === undefined) {
    throw new Error(
      'useAgentSetupContext must be used within an AgentSetupProvider',
    );
  }
  return context;
};

export const AGENT_SETUP_CONTEXT = {
  toast: {
    success: { title: 'Your agent is ready' },
    failed: { title: 'Agent setup failed' },
  },
} as const;
